import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "motion/react";
import RabbitProgress from "../components/RabbitProgress";

export default function BankStatement() {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [clientName, setClientName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [reconciled, setReconciled] = useState<boolean | null>(null);

  const handleUpload = async () => {
    if (!file) { setError("Please select a file"); return; }
    setLoading(true); setError(""); setSuccess(false); setReconciled(null);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("client_name", clientName);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/bank-statement/convert", {
        method: "POST",
        headers: token ? { Authorization: "Bearer " + token } : {},
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Conversion failed");
      }
      const isReconciled = response.headers.get("x-reconciled") !== "false";
      setReconciled(isReconciled);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "TaxThinker_BankStatement_" + (clientName || "Report") + ".xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      (window as any).gtag?.('event', 'report_generated', {
        report_type: 'bank_statement',
        bank_name: file?.name.replace(/\.[^/.]+$/, '') || 'unknown',
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "10px 14px",
    border: "1.5px solid var(--border)",
    borderRadius: "8px", fontSize: "14px",
    color: "var(--text-primary)",
    background: "var(--white)", outline: "none"
  };

  const FEATURES = [
    "bankStatement.feature1",
    "bankStatement.feature2",
    "bankStatement.feature3",
    "bankStatement.feature4",
    "bankStatement.feature5",
    "bankStatement.feature6",
  ];

  return (
    <div>
      <div className="page-header-dark">
        <div className="ph-dotgrid" />
        <div className="ph-glow" />
        <div className="ph-inner">
          <div className="ph-breadcrumb">
            <a href="/">Home</a>
            <span>›</span>
            <span>Tools</span>
            <span>›</span>
            <span style={{ color: "#C9A84C" }}>Bank Statement to Excel</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "8px" }}>
            <span style={{ fontSize: "36px" }}>🏦</span>
            <h1>{t("bankStatement.title")}</h1>
          </div>
          <p className="ph-subtitle">{t("bankStatement.subtitle")}</p>
        </div>
      </div>

      <div style={{ maxWidth: "720px", margin: "0 auto", padding: "40px 24px" }}>

      <div style={{ background: "var(--card-bg)", borderRadius: "16px", border: "1.5px solid var(--border)", padding: "32px" }}>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "6px" }}>
            {t("bankStatement.clientName")}
          </label>
          <input
            type="text"
            value={clientName}
            onChange={e => setClientName(e.target.value)}
            placeholder={t("bankStatement.clientNamePlaceholder")}
            style={inputStyle}
          />
        </div>

        <div
          className="upload-area-premium"
          onClick={() => document.getElementById("bsFileInput")?.click()}
          style={{ marginBottom: "16px" }}
        >
          <input
            id="bsFileInput"
            type="file"
            accept=".pdf,.xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={e => setFile(e.target.files?.[0] || null)}
          />
          <AnimatePresence mode="wait">
            {file ? (
              <motion.div
                key="file-selected"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <div style={{ fontSize: "36px", marginBottom: "8px" }}>
                  {file.name.endsWith(".pdf") ? "📄" : "📊"}
                </div>
                <p style={{ fontWeight: 600, color: "var(--text-primary)", marginBottom: "4px" }}>{file.name}</p>
                <p style={{ fontSize: "12px", color: "var(--text-muted)" }}>{(file.size / 1024).toFixed(1)} KB</p>
                <button
                  onClick={e => { e.stopPropagation(); setFile(null); }}
                  style={{ marginTop: "8px", fontSize: "12px", color: "#C9A84C", background: "transparent", border: "none", cursor: "pointer" }}>
                  {t("bankStatement.changeFile")}
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="upload-prompt"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <div className="upload-icon-bounce" style={{ fontSize: "40px", marginBottom: "12px" }}>
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" style={{ display: "inline-block" }}>
                    <path d="M22 8L22 28M22 8L14 16M22 8L30 16" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 32V36H36V32" stroke="rgba(201,168,76,0.4)" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <p style={{ fontWeight: 700, color: "var(--text-primary)", marginBottom: "6px", fontSize: "18px" }}>
                  Drop your file here
                </p>
                <p style={{ fontSize: "14px", color: "#C9A84C", textDecoration: "underline", marginBottom: "8px" }}>
                  or click to browse
                </p>
                <p style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                  PDF, Excel, CSV up to 10MB
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Trust badges */}
        <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap", marginBottom: "16px" }}>
          {["🔒 Encrypted", "⚡ Processed instantly", "✓ CA-verified format"].map(b => (
            <span key={b} style={{ fontSize: "12px", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "4px" }}>{b}</span>
          ))}
        </div>

        {error && (
          <div style={{ background: "var(--offwhite)", border: "1px solid #FCE4D6", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", fontSize: "13px", color: "#9C0006" }}>
            {error}
          </div>
        )}

        <AnimatePresence>
          {success && (
            <motion.div
              key="success-banner"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              style={{ background: "#E2EFDA", border: "1px solid #A9C9A0", borderRadius: "8px", padding: "14px 16px", marginBottom: "16px", boxShadow: "0 0 12px rgba(46,125,50,0.15)" }}
            >
              <div style={{ fontSize: "14px", fontWeight: 700, color: "#375623", marginBottom: "4px" }}>
                {t("bankStatement.successTitle")}
              </div>
              {reconciled === true && (
                <div style={{ fontSize: "13px", color: "#375623" }}>
                  {t("bankStatement.reconciled")}
                </div>
              )}
              {reconciled === false && (
                <div style={{ fontSize: "13px", color: "#7F3F00" }}>
                  {t("bankStatement.notReconciled")}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          onClick={handleUpload}
          disabled={!file || loading}
          animate={loading ? { opacity: [1, 0.7, 1] } : { opacity: 1 }}
          transition={loading ? { repeat: Infinity, duration: 1.5 } : {}}
          whileHover={!loading && file ? { scale: 1.02 } : {}}
          whileTap={!loading && file ? { scale: 0.98 } : {}}
          style={{ width: "100%", padding: "13px", background: file && !loading ? "#C9A84C" : "var(--border)", color: file && !loading ? "#2C2C2C" : "var(--text-muted)", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: 700, cursor: file && !loading ? "pointer" : "not-allowed", transition: "background 0.2s" }}
        >
          {loading ? t("bankStatement.converting") : t("bankStatement.convert")}
        </motion.button>

        <RabbitProgress loading={loading} estimatedSeconds={45} />

        <div style={{ marginTop: "20px", background: "var(--offwhite)", borderRadius: "10px", padding: "16px" }}>
          <p style={{ fontSize: "13px", fontWeight: 700, color: "var(--text-primary)", marginBottom: "10px" }}>
            {t("bankStatement.yourExcel")}
          </p>
          <div className="form-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {FEATURES.map((key, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "var(--text-secondary)" }}>
                <span style={{ color: "#C9A84C", fontWeight: 700 }}>✓</span> {t(key)}
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: "16px", padding: "12px 16px", background: "#FFF2CC", borderRadius: "8px", fontSize: "12px", color: "#7F6000" }}>
          {t("bankStatement.supportedBanks")}
        </div>
      </div>
      </div>
    </div>
  );
}
